// Third party
import React from 'react';
import classNames from 'classnames';
import { minBy } from 'lodash';

// Components
import { TertiaryButton } from '@/views/components/buttons';
import { InfoTooltip } from '@/views/components/tooltips';
import CarbonIntensity from './CarbonIntensity';
import ComparisonItemSelector from '@/views/containers/comparison/ComparisonItemSelector';
import ESGEmptyData from './ESGEmptyData';
import ESGRanking from './ESGRanking';
import ESGRating from './ESGRating';
import ImpliedTemperatureRise from './ImpliedTemperatureRise';
import ESGNoSelection from './ESGNoSelection';
import DatesDisclaimer from '@/views/components/disclaimers/comparison/DatesDisclaimer';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage, formatMessageWithLocale } from '@/utils/i18n';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import ThemeMode from '@/types/theme-mode';
import { Typography } from '@capintel/component-lib/typography';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  availableComparisonItems: ComparisonItem[];
  selectedItems: ComparisonItem[];
  locale: Locale;
  onRemoveItem: (item: ComparisonItem) => void;
  onSelectItem: (item: ComparisonItem, index: number) => void;
  onScrollToMetrics?: () => void;
  theme?: ThemeMode;
};

const ESGView = ({ availableComparisonItems, selectedItems, locale, onRemoveItem, onSelectItem, onScrollToMetrics, theme }: Props) => {
  const [firstItem, secondItem] = selectedItems || [];

  const handleRemoveItem = (index: number) => {
    selectedItems && onRemoveItem(selectedItems[index]);
  };

  const emptyComparison = !availableComparisonItems || availableComparisonItems.length === 0;

  const getItemLatestDate = (item: ComparisonItem) => {
    if (item?.dates.esgLatestHolding != null) {
      return item.dates.esgLatestHolding;
    }

    if (item?.dates.portfolioESGLatestHolding != null) {
      const dates = Object.values(item.dates.portfolioESGLatestHolding);
      return minBy(dates, e => (e ? new Date(e).getTime() : null));
    }
  };

  const getDisclaimerDates = (items: ComparisonItem[]) =>
    items.map((item: ComparisonItem) => ({
      id: item._id,
      name: formatLocalizedString(item.name, locale),
      dates: [getItemLatestDate(item)] as Date[],
      color: item.color,
      type: item.type,
      holdings: item.holdings,
    }));

  return (
    <div className={classNames('esg-view', { [`${theme}`]: theme })}>
      <div className="esg-view-header">
        <div className="esg-view-title">
          <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
            <FormattedMessage id="ESGComparisonTool.Title" />
            <InfoTooltip
              key="ESGComparisonTool.ESGTitleTooltip"
              title={formatMessage('ESGComparisonTool.Title')}
              message={formatMessage('ESGComparisonTool.Description')}
            />
          </Typography>
        </div>

        <TertiaryButton dark compact onClick={onScrollToMetrics}>
          <FormattedMessage id="ESGComparisonTool.ScrollToDetails" />
          <i className="icon-arrow-down" aria-hidden="true" />
        </TertiaryButton>
      </div>

      <div
        className={classNames('esg-view-module', {
          'esg-view-module-no-data': !selectedItems,
          'is-empty': emptyComparison,
          'is-no-selection': !emptyComparison && selectedItems?.length === 0,
        })}
      >
        {emptyComparison && <ESGEmptyData />}
        {!emptyComparison && selectedItems?.length === 0 && <ESGNoSelection />}
        {!emptyComparison && selectedItems && selectedItems?.length > 0 && (
          <>
            <div className="comparison-item-selections container-fluid">
              {[firstItem, secondItem].map((item, index) => (
                <ComparisonItemSelector
                  availableComparisonItems={availableComparisonItems}
                  key={item?._id ?? `selector-${index}`}
                  onRemoveItem={handleRemoveItem}
                  onSelectItem={onSelectItem}
                  selectedItemIndex={index}
                />
              ))}
            </div>

            <div className="esg-view-module-content container-fluid">
              <div className="esg-view-module-content-left">
                <ESGRanking selectedItems={selectedItems} theme={theme} />
                <ESGRating selectedItems={selectedItems} theme={theme} />
              </div>
              <div className="esg-view-module-content-right">
                <CarbonIntensity selectedItems={selectedItems} theme={theme} />
                <ImpliedTemperatureRise selectedItems={selectedItems} theme={theme} />
              </div>
            </div>

            <div className="esg-view-disclaimer">
              <DatesDisclaimer
                globalPrefix={formatMessageWithLocale('ESGComparisonTool.Disclaimer.Performance', locale)}
                itemPrefix={formatMessageWithLocale('Disclaimers.AsAt', locale)}
                itemDates={getDisclaimerDates(selectedItems)}
                locale={locale}
              />
              <FormattedMessage id="ESGComparisonTool.Disclaimer.Provider" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ESGView;
